import type { KeyboardEvent, ReactNode } from 'react';
import { Fragment, useId } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  ListTreeIcon,
  MoreHorizontalIcon,
  PencilIcon,
  TrashIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActionSheet } from '@/components/ui/phone/ActionSheet';
import { PhoneCard, PhoneCardList } from '@/components/ui/phone/PhoneCardList';
import { DeletedLabel } from '@/shared/components/DeletedLabel';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { LLM_PROFILES_TABLE_COLUMN_COUNT } from '@/features/organization-admin';
import {
  formatMoney,
  THRESHOLD_CHIP,
  THRESHOLD_CHIP_BOX,
} from '@/features/workspace-usage';

import { EFFORT_LABELS } from '../effortLabels';
import {
  defaultKind,
  entryKey,
  toLlmProfile,
  type DeletedLlmProfileRow,
  type LlmProfileGridEntry,
  type LlmProfileRow,
  type LlmProfilesSort,
  type ProfileSortKey,
} from '../llmProfileRows';
import type {
  LlmProfile,
  LlmProfileAdminOverviewProvider,
  ModelPriceView,
  UnpricedModelView,
} from '../types';
import { LlmProfileRowDetail } from './LlmProfileRowDetail';
import { SetDefaultProfileControl } from './SetDefaultProfileControl';

export interface LlmProfilesTableProps {
  /**
   * The rows actually displayed — already filtered and already ordered, with any
   * deleted profile that still has usage placed among them (spec 258 FR-004).
   */
  rows: readonly LlmProfileGridEntry[];
  /**
   * `model → how many profiles in the whole response share it`, from
   * `profileCountsByModel`. Over the **unfiltered** response, because "is this the
   * only profile for its model" is a fact about the registry rather than about what
   * the reader searched for (FR-009).
   */
  counts: ReadonlyMap<string, number>;
  /** The profile whose sheet is open. */
  selectedSlug?: string;
  onSelect: (slug: string) => void;
  /** The one expanded row, or `null` — at most one (FR-001a). */
  expandedSlug: string | null;
  onToggleExpand: (slug: string) => void;
  /**
   * The order to show. A **prop**, never a local copy: it lives in the address
   * (FR-011), and a second copy here is how a bookmarked order and the arrow in the
   * header start disagreeing.
   */
  sort: LlmProfilesSort;
  /** `undefined` means "back to the default order", which the address omits. */
  onSortChange?: (sort: LlmProfilesSort | undefined) => void;
  /**
   * `model_key → current price`, `undefined` while the organization-wide price lookup
   * is still unresolved (spec 092 FR-001a). A model absent from a resolved map has no
   * current price. Looked up by the row's **`model`**, never by its slug, so two
   * profiles on one model legitimately show the same cell (FR-016).
   */
  priceLookup: Map<string, ModelPriceView> | undefined;
  /**
   * The price read **failed**, as opposed to not having answered yet.
   *
   * Without this, `undefined` did double duty for "pending" and "failed" and the whole
   * Rate column stayed blank forever on a 403 or a 500 — no chip, no action, nothing
   * to retry (R-F4). The cell says so instead.
   */
  priceLookupFailed: boolean;
  /** `model_key → this period's unpriced-run info`, same resolution rule. */
  unpricedRunsLookup: Map<string, UnpricedModelView> | undefined;
  /**
   * The unpriced-run read failed. A separate flag rather than folded into
   * {@link LlmProfilesTableProps.priceLookupFailed}: the run count is a detail
   * *beneath* the chip, so losing it must not also hide a rate that loaded fine.
   * It still cannot be silent — a missing count and a count of zero would otherwise
   * render identically (FR-007).
   */
  unpricedRunsLookupFailed: boolean;
  /** The model whose inline price form is open — keyed by model key, not slug. */
  pricingModel: string | null;
  onPriceFormOpen: (model: string | null) => void;
  onEdit: (entry: LlmProfile) => void;
  onDelete: (entry: LlmProfile) => void;
}

/**
 * The connection's status, in this surface's words (`contracts/ui-contract.md`
 * § "Provider cell").
 *
 * **Deliberately not `AiProviderList`'s `STATUS_BADGE`, and this is a known
 * disagreement**: that one reads `Untested` / `Connected` / `Failed`, the UI contract
 * asks for `Not tested` / `Connected` / `Connection failed`. The contract is this
 * feature's authority, so it wins here; reconciling the two surfaces is flagged for the
 * human copy review (T022). The colour treatment is the neighbour's, unchanged — a
 * second visual language for one state would be the worse drift of the two.
 */
const CONNECTION_STATUS: Record<
  LlmProfileAdminOverviewProvider['connection_status'],
  { label: string; variant: 'secondary' | 'destructive'; className?: string }
> = {
  untested: { label: 'Not tested', variant: 'secondary' },
  ok: {
    label: 'Connected',
    variant: 'secondary',
    className: 'bg-board-state-done/12 text-board-state-done',
  },
  failed: { label: 'Connection failed', variant: 'destructive' },
};

/** A status badge for any value the service sends, known or not (R-F34). */
function connectionStatusBadge(
  status: LlmProfileAdminOverviewProvider['connection_status'],
): { label: string; variant: 'secondary' | 'destructive'; className?: string } {
  const known = CONNECTION_STATUS[status];
  if (known) return known;
  // The generated enum is closed, so TypeScript is satisfied by the bare indexing and a
  // fourth status added to the contract arrived as `undefined` — `status.label` then
  // threw and took the whole page into the error boundary, with nothing naming the
  // value. The raw value is the honest label: it is what the service calls this state.
  reportUnknownEnumValue('connection_status', status);
  return { label: status, variant: 'secondary' };
}

/** The effort label, or the raw value for one this build does not know (R-F34). */
function effortLabel(effort: LlmProfileRow['effort']): string {
  const known = EFFORT_LABELS[effort];
  if (known) return known;
  reportUnknownEnumValue('effort', effort);
  return effort;
}

/**
 * Values already reported, so one unknown enum on a table that re-renders is one line
 * in the console rather than a stream of them — `shared/utils/time.ts`'s
 * `reportUnparsable` has the same shape for the same reason.
 */
const reportedUnknownEnumValues = new Set<string>();

function reportUnknownEnumValue(field: string, value: string): void {
  const seen = `${field}:${value}`;
  if (reportedUnknownEnumValues.has(seen)) return;
  reportedUnknownEnumValues.add(seen);
  console.warn(
    `The LLM profiles grid has no wording for ${field} ${JSON.stringify(value)}; showing the raw value. The contract has probably gained a value this build does not know.`,
  );
}

/**
 * Every LLM profile in the organization, one per row (spec 136 US1).
 *
 * Hand-rolled and semantic, structured after `WorkspaceLimitsTable`: the same
 * container, the same `sr-only` caption, the same clickable `<tr>` carrying
 * `aria-selected` and its own focus ring, and the same local `SortHeader`. No
 * `@tanstack/react-table` — ten columns of plain markup is less code than the
 * configuration a table library would need, and it keeps the header row something the
 * column-count guard can read at the source level (FR-002).
 *
 * The four `--phase-accent-*` model hues die with the grouping this replaces and no
 * replacement hue is invented: a flat table tells rows apart by their cells, which is
 * what made the per-model colour worth carrying in the first place.
 */
export function LlmProfilesTable({
  rows,
  counts,
  selectedSlug,
  onSelect,
  expandedSlug,
  onToggleExpand,
  sort,
  onSortChange,
  priceLookup,
  priceLookupFailed,
  unpricedRunsLookup,
  unpricedRunsLookupFailed,
  pricingModel,
  onPriceFormOpen,
  onEdit,
  onDelete,
}: LlmProfilesTableProps) {
  // One base id, composed with the slug per row: `useId` cannot be called inside the
  // map, and the slug is the row's own identity anyway (Edge Cases — the registry keys
  // on `slug`).
  const detailIdBase = useId();
  const isPhone = usePhoneWidth();

  if (isPhone) {
    return (
      <LlmProfileCards
        rows={rows}
        counts={counts}
        selectedSlug={selectedSlug}
        onSelect={onSelect}
        expandedSlug={expandedSlug}
        onToggleExpand={onToggleExpand}
        sort={sort}
        priceLookup={priceLookup}
        priceLookupFailed={priceLookupFailed}
        unpricedRunsLookup={unpricedRunsLookup}
        unpricedRunsLookupFailed={unpricedRunsLookupFailed}
        pricingModel={pricingModel}
        onPriceFormOpen={onPriceFormOpen}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="border-border bg-card overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[76rem] text-sm">
        <caption className="sr-only">
          Every LLM profile, what it points at and what it costs
        </caption>
        <thead>
          <tr className="text-muted-foreground border-border border-b text-xs">
            <th scope="col" className="px-2 py-2 text-left font-medium">
              <span className="sr-only">Expand</span>
            </th>
            <SortHeader
              label="Profile"
              column="name"
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortHeader
              label="Provider"
              column="provider"
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortHeader
              label="Model"
              column="model"
              sort={sort}
              onSortChange={onSortChange}
            />
            <th scope="col" className="px-4 py-2 text-left font-medium">
              Settings
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              Rate
            </th>
            {/*
              The window is in the header on purpose (decision 2A, plan risk 6): every
              other usage surface in this app is period-scoped, and a reader who has
              internalised that would otherwise read this column as "this period".
            */}
            <SortHeader
              label="Spend (all time)"
              column="spend"
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortHeader
              label="Workspaces"
              column="workspaces"
              sort={sort}
              onSortChange={onSortChange}
            />
            <SortHeader
              label="Workflows"
              column="workflows"
              sort={sort}
              onSortChange={onSortChange}
            />
            <th scope="col" className="px-4 py-2 text-left font-medium">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.deleted) {
              return <DeletedProfileTableRow key={entryKey(row)} row={row} />;
            }
            const selected = row.slug === selectedSlug;
            // Only for a row actually on screen: a filter or a re-sort that drops the
            // expanded row must not leave its detail cell orphaned beneath a different
            // profile (Edge Cases).
            const expanded = row.slug === expandedSlug;
            const detailRowId = `${detailIdBase}-${row.slug}`;
            const profile = toLlmProfile(row);
            const price =
              priceLookup === undefined ? null : priceLookup.get(row.model);
            const kind = defaultKind(row, counts);
            const status = connectionStatusBadge(
              row.ai_provider.connection_status,
            );

            return (
              <Fragment key={row.slug}>
                <tr
                  data-profile-slug={row.slug}
                  tabIndex={0}
                  aria-selected={selected}
                  onClick={() => {
                    onSelect(row.slug);
                  }}
                  onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(row.slug);
                    }
                  }}
                  className={`border-border focus-visible:ring-ring cursor-pointer border-t first:border-t-0 focus-visible:ring-3 focus-visible:outline-none ${
                    selected ? 'bg-selected' : 'hover:bg-accent/50'
                  }`}
                >
                  <td className="px-2 py-2.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-expanded={expanded}
                      aria-controls={detailRowId}
                      onClick={(event) => {
                        // Expanding is a separate affordance from opening the sheet
                        // (FR-001a): without this the row's own handler fires too and
                        // the detail a reader just asked for appears behind a sheet.
                        event.stopPropagation();
                        onToggleExpand(row.slug);
                      }}
                      onKeyDown={(event) => {
                        // Enter and Space as well as the click (R-F5): the row answers
                        // both, so a keyboard user pressing Enter on the chevron
                        // expanded the row *and* opened the sheet over the detail they
                        // had just asked for. `RowActions` does the same for the
                        // trailing cell; the chevron is not inside one.
                        event.stopPropagation();
                      }}
                    >
                      <ChevronRightIcon
                        aria-hidden="true"
                        className={
                          expanded
                            ? 'size-4 rotate-90 transition-transform'
                            : 'size-4 transition-transform'
                        }
                      />
                      <span className="sr-only">
                        {expanded ? 'Hide' : 'Show'} what uses {row.name}
                      </span>
                    </Button>
                  </td>

                  <th scope="row" className="px-4 py-2.5 text-left font-medium">
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate">{row.name}</span>
                      {/*
                      Two visibly different badges, not one (FR-009): "chosen" and
                      "the only candidate" are different facts, and a single badge
                      would tell an admin they had made a decision they never made.
                    */}
                      {kind === 'none' ? null : (
                        <span>
                          <Badge variant="secondary">
                            {kind === 'explicit'
                              ? 'Default'
                              : 'Default (only one)'}
                            <span className="sr-only">
                              {kind === 'explicit'
                                ? ' for this model, chosen'
                                : ' for this model, by default'}
                            </span>
                          </Badge>
                        </span>
                      )}
                    </span>
                  </th>

                  <td className="px-4 py-2.5">
                    {/*
                    A failed or untested connection shows its status and keeps its row
                    (Edge Cases) — hiding it would hide the profile that is about to
                    stop working.
                  */}
                    <span className="flex min-w-0 flex-col items-start gap-0.5">
                      <span className="truncate">{row.ai_provider.name}</span>
                      <Badge
                        variant={status.variant}
                        className={status.className}
                      >
                        {status.label}
                      </Badge>
                    </span>
                  </td>

                  <td className="text-muted-foreground px-4 py-2.5 font-mono text-xs">
                    {row.model}
                  </td>

                  <td className="px-4 py-2.5">
                    <span className="flex min-w-0 flex-col items-start gap-0.5">
                      <Badge variant="secondary">
                        {effortLabel(row.effort)}
                      </Badge>
                      {/* `null` is stated, never blank (Edge Cases). */}
                      <span className="text-muted-foreground text-xs">
                        {turnsText(row.max_turns)}
                      </span>
                    </span>
                  </td>

                  <td className="px-4 py-2.5">
                    <RateCell
                      model={row.model}
                      price={price}
                      priceFailed={priceLookupFailed}
                      unpricedRuns={unpricedRunsLookup?.get(row.model)}
                      unpricedRunsFailed={unpricedRunsLookupFailed}
                      pricing={pricingModel === row.model}
                      onOpen={() => {
                        // The form lives in the detail row, so opening it has to open
                        // that row too — the same coupling the grouped list's row had with
                        // its collapsible panel, restated for a detail `<tr>`.
                        if (!expanded) onToggleExpand(row.slug);
                        onPriceFormOpen(row.model);
                      }}
                    />
                  </td>

                  <td className="px-4 py-2.5">
                    <span className="flex flex-col gap-1">
                      <span className="tabular-nums">
                        {/*
                        `spend.amount` is the exact decimal string the service holds in
                        `NUMERIC`. `formatMoney` truncates it as a string; `Number()` or
                        `parseFloat` here is the one step that would reintroduce the
                        imprecision the backend went to trouble to avoid (FR-005).
                        `currency` is optional on the contract and `formatMoney`'s own
                        parameter already defaults to USD, so it is passed straight
                        through rather than defaulted twice.
                      */}
                        {formatMoney(row.spend.amount, row.spend.currency)}
                      </span>
                      <ExclusionCounts spend={row.spend} />
                    </span>
                  </td>

                  <td className="text-muted-foreground px-4 py-2.5 text-xs tabular-nums">
                    {/* `None`, never a blank: "zero" and "I don't know" must not
                      render identically (FR-007). */}
                    {countText(row.usage.workspace_count, [
                      'workspace',
                      'workspaces',
                    ])}
                  </td>

                  <td className="text-muted-foreground px-4 py-2.5 text-xs tabular-nums">
                    {countText(row.usage.workflow_count, [
                      'workflow',
                      'workflows',
                    ])}
                  </td>

                  <td className="px-4 py-2.5">
                    {/*
                    The controls are unchanged (FR-018); what is new is the wrapper.
                    A menu item or "Set as default" inside a clickable row would
                    otherwise also activate the row, so every action would open a sheet
                    on its way to doing something else (FR-001b).
                  */}
                    <RowActions>
                      <SetDefaultProfileControl
                        profile={profile}
                        siblingCount={counts.get(row.model) ?? 1}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                            >
                              <MoreHorizontalIcon aria-hidden="true" />
                              <span className="sr-only">
                                Actions for {row.name}
                              </span>
                            </Button>
                          }
                        />
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              onEdit(profile);
                            }}
                          >
                            <PencilIcon aria-hidden="true" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => {
                              onDelete(profile);
                            }}
                          >
                            <TrashIcon aria-hidden="true" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </RowActions>
                  </td>
                </tr>
                {/*
                  The detail row, a sibling `<tr>` rather than content inside the
                  first — a clickable row cannot contain a region a reader is meant to
                  read without activating it. Rendered **only** for a row actually on
                  screen, which is what the `expanded` check above buys: a filter or a
                  re-sort that drops the expanded profile cannot leave its detail cell
                  attached to a different one.
                */}
                {expanded ? (
                  <tr id={detailRowId}>
                    <td
                      colSpan={LLM_PROFILES_TABLE_COLUMN_COUNT}
                      className="border-border bg-muted/30 border-t px-4 py-4"
                    >
                      <LlmProfileRowDetail
                        row={row}
                        price={price ?? undefined}
                        pricing={pricingModel === row.model}
                        onPricingDone={() => {
                          onPriceFormOpen(null);
                        }}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The profiles as cards on a phone (spec 263), where the table was 76rem wide.
 *
 * The name with its Default and connection badges; provider and model; effort and
 * turns; spend and what uses it. The rate and "Set as default" stay on the card,
 * as controls of their own; Edit, Delete and "What uses it" go behind ⋯. The
 * detail a desktop opens beneath the row opens inside the card.
 */
function LlmProfileCards({
  rows,
  counts,
  selectedSlug,
  onSelect,
  expandedSlug,
  onToggleExpand,
  priceLookup,
  priceLookupFailed,
  unpricedRunsLookup,
  unpricedRunsLookupFailed,
  pricingModel,
  onPriceFormOpen,
  onEdit,
  onDelete,
}: LlmProfilesTableProps) {
  return (
    <PhoneCardList label="Every LLM profile, what it points at and what it costs">
      {rows.map((row) => {
        if (row.deleted) {
          return <DeletedProfileCard key={entryKey(row)} row={row} />;
        }
        const expanded = row.slug === expandedSlug;
        const profile = toLlmProfile(row);
        const price =
          priceLookup === undefined ? null : priceLookup.get(row.model);
        const kind = defaultKind(row, counts);
        const status = connectionStatusBadge(row.ai_provider.connection_status);
        return (
          <PhoneCard
            key={row.slug}
            data-profile-slug={row.slug}
            selected={row.slug === selectedSlug}
            onOpen={() => {
              onSelect(row.slug);
            }}
            title={row.name}
            badges={
              <>
                {kind === 'none' ? null : (
                  <Badge variant="secondary">
                    {kind === 'explicit' ? 'Default' : 'Default (only one)'}
                    <span className="sr-only">
                      {kind === 'explicit'
                        ? ' for this model, chosen'
                        : ' for this model, by default'}
                    </span>
                  </Badge>
                )}
                <Badge variant={status.variant} className={status.className}>
                  {status.label}
                </Badge>
              </>
            }
            actions={
              <ActionSheet
                name={row.name}
                actions={[
                  {
                    label: expanded ? 'Hide what uses it' : 'What uses it',
                    icon: ListTreeIcon,
                    onSelect: () => {
                      onToggleExpand(row.slug);
                    },
                  },
                  {
                    label: 'Edit',
                    icon: PencilIcon,
                    onSelect: () => {
                      onEdit(profile);
                    },
                  },
                  {
                    label: 'Delete',
                    icon: TrashIcon,
                    destructive: true,
                    onSelect: () => {
                      onDelete(profile);
                    },
                  },
                ]}
              />
            }
            lines={[
              <span key="model">
                {row.ai_provider.name} ·{' '}
                <span className="font-mono text-xs [overflow-wrap:anywhere]">
                  {row.model}
                </span>
              </span>,
              `${effortLabel(row.effort)} effort · ${turnsText(row.max_turns)}`,
              <span key="spend" className="flex flex-col gap-1">
                <span className="tabular-nums">
                  {formatMoney(row.spend.amount, row.spend.currency)} all time ·{' '}
                  {countText(row.usage.workspace_count, [
                    'workspace',
                    'workspaces',
                  ])}{' '}
                  ·{' '}
                  {countText(row.usage.workflow_count, [
                    'workflow',
                    'workflows',
                  ])}
                </span>
                <ExclusionCounts spend={row.spend} />
              </span>,
            ]}
            footer={
              <span className="flex flex-wrap items-center gap-2">
                <RateCell
                  model={row.model}
                  price={price}
                  priceFailed={priceLookupFailed}
                  unpricedRuns={unpricedRunsLookup?.get(row.model)}
                  unpricedRunsFailed={unpricedRunsLookupFailed}
                  pricing={pricingModel === row.model}
                  onOpen={() => {
                    if (!expanded) onToggleExpand(row.slug);
                    onPriceFormOpen(row.model);
                  }}
                />
                <SetDefaultProfileControl
                  profile={profile}
                  siblingCount={counts.get(row.model) ?? 1}
                />
              </span>
            }
          >
            {expanded ? (
              <div className="border-border relative z-10 mt-2 border-t pt-3">
                <LlmProfileRowDetail
                  row={row}
                  price={price ?? undefined}
                  pricing={pricingModel === row.model}
                  onPricingDone={() => {
                    onPriceFormOpen(null);
                  }}
                />
              </div>
            ) : null}
          </PhoneCard>
        );
      })}
    </PhoneCardList>
  );
}

/** Said where a live profile's provider, model, settings and rate would be. */
const DELETED_PROFILE_NOTE =
  'Deleted. Its spend still counts here, under its last name.';

/**
 * A deleted profile that still has usage (spec 258 FR-004): its name, the Deleted
 * marker and its spend, muted, with nothing to open, expand or act on. There is no
 * profile left to edit, and its provider and model went with it.
 */
function DeletedProfileTableRow({ row }: { row: DeletedLlmProfileRow }) {
  return (
    <tr
      data-deleted-profile={row.key}
      className="border-border text-muted-foreground border-t first:border-t-0"
    >
      <td className="px-2 py-2.5" />
      <th scope="row" className="px-4 py-2.5 text-left font-medium">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{row.name}</span>
          <DeletedLabel />
        </span>
      </th>
      <td colSpan={4} className="px-4 py-2.5 text-xs">
        {DELETED_PROFILE_NOTE}
      </td>
      <td className="px-4 py-2.5">
        <span className="flex flex-col gap-1">
          <span className="tabular-nums">
            {formatMoney(row.spend.amount, row.spend.currency)}
          </span>
          <ExclusionCounts spend={row.spend} />
        </span>
      </td>
      <td className="px-4 py-2.5 text-xs tabular-nums">None</td>
      <td className="px-4 py-2.5 text-xs tabular-nums">None</td>
      <td className="px-4 py-2.5" />
    </tr>
  );
}

/** The same deleted profile as a phone card: no ⋯, no tap target. */
function DeletedProfileCard({ row }: { row: DeletedLlmProfileRow }) {
  return (
    <PhoneCard
      data-deleted-profile={row.key}
      title={row.name}
      badges={<DeletedLabel />}
      lines={[
        DELETED_PROFILE_NOTE,
        <span key="spend" className="flex flex-col gap-1">
          <span className="tabular-nums">
            {formatMoney(row.spend.amount, row.spend.currency)} all time
          </span>
          <ExclusionCounts spend={row.spend} />
        </span>,
      ]}
    />
  );
}

/** `Max 7 turns` / `Max 1 turn` / `No turn limit` — the `null` case is stated. */
function turnsText(maxTurns: number | null): string {
  if (maxTurns === null) return 'No turn limit';
  return maxTurns === 1 ? 'Max 1 turn' : `Max ${String(maxTurns)} turns`;
}

/** A usage count, or the explicit `None` that a blank cell would be mistaken for. */
function countText(count: number, [one, many]: [string, string]): string {
  if (count === 0) return 'None';
  return `${String(count)} ${count === 1 ? one : many}`;
}

function runs(count: number): string {
  return count === 1 ? '1 run' : `${String(count)} runs`;
}

/**
 * Keeps a cell's controls from activating the row they sit in (FR-001b).
 *
 * `onKeyDown` as well as `onClick`: the row answers Enter and Space, and so does every
 * button inside it, so a keyboard user pressing Enter on "Set as default" would
 * otherwise set the default *and* open the sheet.
 */
function RowActions({ children }: { children: ReactNode }) {
  return (
    <span
      className="flex items-center justify-end gap-2"
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
    >
      {children}
    </span>
  );
}

/**
 * What is missing from the figure beside it, named per cause (FR-006, R7).
 *
 * Three causes, three sentences, in a fixed order, and **nothing at all** when all
 * three are zero: a permanently visible reassurance is a line readers learn to skip,
 * and these need to be read on the day they appear (`IncompletenessNotice`'s own
 * reasoning, which the first two sentences are borrowed from verbatim-in-substance).
 *
 * The third cause — runs that predate profile attribution — is new here and offers no
 * fix, because it has none: those runs will never be attributed.
 */
function ExclusionCounts({ spend }: { spend: LlmProfileRow['spend'] }) {
  const {
    unmeasured_run_count: unmeasured,
    unpriced_run_count: unpriced,
    unattributed_run_count: unattributed,
  } = spend;
  if (unmeasured === 0 && unpriced === 0 && unattributed === 0) return null;

  return (
    <span className="text-muted-foreground flex flex-col gap-0.5 text-xs">
      {unmeasured > 0 ? (
        <span>
          {runs(unmeasured)} could not be measured, so nothing they consumed is
          counted here.
        </span>
      ) : null}
      {unpriced > 0 ? (
        <span>
          {runs(unpriced)} ran on a model with no price for that date, so their
          tokens are counted and their cost is not.
        </span>
      ) : null}
      {unattributed > 0 ? (
        <span>
          {runs(unattributed)} are not attributed to this profile — they ran
          before profiles were recorded.
        </span>
      ) : null}
    </span>
  );
}

/**
 * The model's rate, or the gap where one should be (FR-016) — unchanged in substance
 * from specs 092/094, re-homed from a group header to a column.
 *
 * A price belongs to the **model**, so this is looked up by the row's `model` and two
 * profiles sharing one show the same cell. The chip's `sr-only` suffix says so, because
 * two identical chips on adjacent rows otherwise read as a coincidence.
 */
function RateCell({
  model,
  /** The model's price, `undefined` if none is recorded, `null` while unresolved. */
  price,
  priceFailed,
  unpricedRuns,
  unpricedRunsFailed,
  pricing,
  onOpen,
}: {
  model: string;
  price: ModelPriceView | undefined | null;
  priceFailed: boolean;
  unpricedRuns: UnpricedModelView | undefined;
  unpricedRunsFailed: boolean;
  pricing: boolean;
  onOpen: () => void;
}) {
  // Checked **before** the unresolved silence below, because a failed read also leaves
  // the lookup `undefined` forever: without this the column stayed permanently blank on
  // a 403 or a 500 and a reader could not tell a dead price service from a slow one
  // (R-F4). The rate is this one column's answer, so saying so here is enough — the
  // page's own load-failed state belongs to the profiles read, which succeeded.
  if (priceFailed) {
    return (
      <span className="text-muted-foreground text-xs">
        The rate could not be loaded.
        <span className="sr-only"> for this model</span>
      </span>
    );
  }

  // Nothing renders while the organization-wide lookup is unresolved — neither the chip
  // nor the action. Flashing "No price" on a model that turns out to be priced would
  // undermine the very convention the chip borrows (spec 092 FR-001a), and that silence
  // is the easiest part of this cell to lose.
  if (price === null) return null;

  if (price === undefined) {
    return (
      <span className="flex flex-col items-start gap-1">
        <span className={`${THRESHOLD_CHIP_BOX} ${THRESHOLD_CHIP.exhausted}`}>
          No price
          <span className="sr-only"> for this model</span>
        </span>
        {/*
          Beneath the chip, not beside it: the run count is a detail worth reading once
          the chip has been noticed, not something that competes with it.
        */}
        {unpricedRunsFailed ? (
          // Said, not omitted: "no runs went unpriced" and "we could not find out"
          // must not render identically (FR-007, R-F4).
          <span className="text-muted-foreground text-xs">
            How many runs went unpriced could not be loaded.
          </span>
        ) : unpricedRuns ? (
          <span className="text-muted-foreground text-xs">
            {runs(unpricedRuns.run_count)} on this model with no price,{' '}
            {unpricedRuns.total_tokens.toLocaleString()} tokens.
          </span>
        ) : null}
        {/*
          Hidden while this model's form is open, not merely disabled — the form
          carries its own Cancel, and a visible "Add rate" beside an open "Add rate"
          form reads as two competing controls for one action.
        */}
        {pricing ? null : (
          <RateAction label="Add rate" model={model} onOpen={onOpen} />
        )}
      </span>
    );
  }

  return (
    <span className="flex flex-col items-start gap-1">
      <span className="tabular-nums">
        {formatMoney(price.input_rate)} in · {formatMoney(price.output_rate)}{' '}
        out
      </span>
      <span className="text-muted-foreground text-xs">per million tokens</span>
      {pricing ? null : (
        <RateAction
          label="Change rate"
          model={model}
          variant="outline"
          onOpen={onOpen}
        />
      )}
    </span>
  );
}

/**
 * Either rate action — `stopPropagation` so neither one also opens the sheet.
 *
 * On `onKeyDown` as well as `onClick` (R-F5): the clickable row answers Enter and
 * Space too, so a keyboard user pressing Enter on "Add rate" opened the edit sheet on
 * top of the form they had just asked for.
 */
function RateAction({
  label,
  model,
  variant,
  onOpen,
}: {
  label: string;
  model: string;
  variant?: 'outline';
  onOpen: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
    >
      {label}{' '}
      {/* Two rows on one model offer the same action; the name says which thing it
          acts on so the duplicate does not read as an accident.

          The separating space sits **outside** the `sr-only` span, not inside it as a
          leading space: accessible-name computation trims each descendant's text before
          joining, so " for claude-opus-9" inside the span collapsed the name to
          "Add ratefor claude-opus-9" (R-A11y). */}
      <span className="sr-only">for {model}</span>
    </Button>
  );
}

/**
 * A column header that orders the table: first press largest-first (A→Z for the `name`
 * column), then the reverse, then off — where "off" is the default order, name
 * ascending, which the address omits.
 *
 * **Deliberately a local duplicate** of `WorkspaceLimitsTable`'s, not a hoisted shared
 * component (research R4). It is generic over *this* table's key union, and that union
 * is the whole of what makes it safe: a shared version would have to be generic over an
 * open string or carry both tables' keys, and either way a key this grid cannot sort by
 * would type-check its way into a header. Twenty lines of markup is the cheaper half of
 * that trade.
 */
function SortHeader({
  label,
  column,
  sort,
  onSortChange,
}: {
  label: string;
  column: ProfileSortKey;
  sort: LlmProfilesSort;
  onSortChange?: (sort: LlmProfilesSort | undefined) => void;
}) {
  const active = sort.key === column;
  const ariaSort = active
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  if (!onSortChange) {
    return (
      <th scope="col" className="px-4 py-2 text-left font-medium">
        {label}
      </th>
    );
  }
  // Name reads A→Z first because that is the order the registry already has; every
  // other column reads largest-first, because a reader sorting by spend or usage is
  // looking for the top of the list.
  const firstDir = column === 'name' ? 'asc' : 'desc';
  const next: LlmProfilesSort | undefined = !active
    ? { key: column, dir: firstDir }
    : sort.dir === firstDir
      ? { key: column, dir: firstDir === 'asc' ? 'desc' : 'asc' }
      : undefined;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="px-4 py-2 text-left font-medium"
    >
      <button
        type="button"
        onClick={() => {
          onSortChange(next);
        }}
        className="hover:text-foreground focus-visible:ring-ring touch-target inline-flex items-center gap-1 rounded-sm focus-visible:ring-3 focus-visible:outline-none"
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUpIcon aria-hidden="true" className="size-3" />
          ) : (
            <ArrowDownIcon aria-hidden="true" className="size-3" />
          )
        ) : null}
      </button>
    </th>
  );
}
